import type {BadgeDataSnapshot} from 'src/api/ProactApiClient';

export interface BadgeSnapshotDiffEntry {
  field: string;
  label: string;
  before: string;
  after: string;
}

const TEXT_FIELDS: {
  field: keyof BadgeDataSnapshot;
  label: string;
}[] = [
  {field: 'personnelName', label: 'Name'},
  {field: 'firstName', label: 'First Name'},
  {field: 'lastName', label: 'Last Name'},
  {field: 'employeeId', label: 'Employee ID'},
  {field: 'companyName', label: 'Company Name'},
  {field: 'badgeExpirationDate', label: 'Badge Expiration'},
  {field: 'instanceName', label: 'Instance'},
  {field: 'generatedDate', label: 'Generated Date'},
];

const EMPTY_DISPLAY = '(empty)';

const displayValue = (value: unknown): string => {
  if (value === undefined || value === null || value === '') {
    return EMPTY_DISPLAY;
  }
  return String(value);
};

// Photo diffs use `hasPhoto`/`photoHash` — the snapshot deliberately never
// stores the photo bytes themselves.
export function diffBadgeSnapshots(
  before: BadgeDataSnapshot | undefined,
  after: BadgeDataSnapshot | undefined
): BadgeSnapshotDiffEntry[] {
  if (!before || !after) return [];

  const entries: BadgeSnapshotDiffEntry[] = [];

  for (const {field, label} of TEXT_FIELDS) {
    const beforeValue = before[field];
    const afterValue = after[field];
    if (beforeValue !== afterValue) {
      entries.push({
        field,
        label,
        before: displayValue(beforeValue),
        after: displayValue(afterValue),
      });
    }
  }

  if (before.templateName !== after.templateName || before.templateVersion !== after.templateVersion) {
    entries.push({
      field: 'template',
      label: 'Template',
      before: `${displayValue(before.templateName)} (v${before.templateVersion})`,
      after: `${displayValue(after.templateName)} (v${after.templateVersion})`,
    });
  }

  if (before.hasPhoto !== after.hasPhoto || before.photoHash !== after.photoHash) {
    entries.push({
      field: 'photo',
      label: 'Photo',
      before: before.hasPhoto ? 'Present' : 'None',
      after: after.hasPhoto ? 'Present' : 'None',
    });
  }

  if (before.autoGenerated !== after.autoGenerated) {
    entries.push({
      field: 'autoGenerated',
      label: 'Generation Type',
      before: before.autoGenerated ? 'Auto' : 'Manual',
      after: after.autoGenerated ? 'Auto' : 'Manual',
    });
  }

  return entries;
}
